import {
  Box,
  Divider,
  Flex,
  PrimaryButton,
  RoundedButton,
  SecondaryButton,
  SystemProps,
  Text,
} from "flicket-ui";
import { useContext } from "react";
import styled from "styled-components";

import { Icon } from "~components/common/Icon";
import {
  AlcoholFreeZoneTypes,
  CoverTypes,
  SeatSelectTypes,
} from "~components/reservation/constants";
import { formatSeatLocation } from "~lib";

import { Placeholder } from "~components/reservation/options/options.Placeholder";
import { useReservationStore } from "~features/seated-reservation/store";
import { EventQuery, IntegrationType } from "~graphql/sdk";
import { Pricing } from "../components/sidebar/PricingList";
import { List } from "../components/sidebar/ZoneSectionList";
import { Release } from "./../types";

import { UserContext } from "~context";
import * as actions from "../store/actions/index";
import useActiveReferralCampaign from "~features/referrals/useActiveReferralCampaign";
import { isBNPLIntegrationType } from "~features/checkout/checkout.types";

type SidebarProps = {
  event: EventQuery["event"];
  release: Release;
  changeTickets: any[];
  handleOrderCreation: () => Promise<boolean>;
  priceComp: number | undefined;
  allowPublicComp?: boolean;
};

const StyledRoundedButton = styled(RoundedButton)<SystemProps>`
  height: 32px;
  width: 32px;

  display: flex !important;
  align-items: center;
  justify-content: center;

  border: 1px solid ${(p) => p.theme.colors.N300};

  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.03),
    0px 1.6711px 8px rgba(0, 0, 0, 0.04), 0px 0.893452px 5px rgba(0, 0, 0, 0.03),
    0px 0.500862px 3.63125px rgba(0, 0, 0, 0.02),
    0px 0.266004px 1.92853px rgba(0, 0, 0, 0.015),
    0px 0.11069px 0.802504px rgba(0, 0, 0, 0.01);
`;

const OriginalSeatBox = styled(Box)`
  border-radius: 6px;
  background: ${(p) => p.theme.colors.P100};
  padding: 16px;
`;

const SidebarLayout = ({
  event,
  release,
  changeTickets,
  handleOrderCreation,
  priceComp,
  allowPublicComp = false,
}: SidebarProps) => {
  const seats = useReservationStore((state) => state.seats);
  const sidebarIndex = useReservationStore((state) => state.sidebarIndex);
  const options = useReservationStore((state) => state.options);
  const chartReady = useReservationStore((state) => state.chartReady);
  const currentZone = useReservationStore((state) => state.currentZone);
  const currentSection = useReservationStore((state) => state.currentSection);
  const selectableZones = useReservationStore((state) => state.selectableZones);
  const busyState = useReservationStore((state) => state.busyState);
  const validSeatSelection = useReservationStore(
    (state) => state.validSeatSelection
  );

  const { isAdmin, isPOS } = useContext(UserContext);

  const addons = release?.releaseEventAddons.map(
    ({ eventAddon }) => eventAddon
  );

  const { activeReferralCampaign } = useActiveReferralCampaign(event.id);

  const handleBackButton = async () => {
    await actions.onSidebarBack();
  };

  const navigateCheckout = async () => {
    actions.setBusyState({ state: "creating-order" });

    const success = await handleOrderCreation();

    if (!success) {
      actions.setBusyState({ state: "idle" });
    }
  };

  const bnplGateways = event.gateways?.filter(
    (gateway) => !!gateway && isBNPLIntegrationType(gateway.type)
  );

  const stripeAfterpayGateway = event.gateways?.find(
    (gateway) =>
      gateway.type === IntegrationType.PaymentStripe && gateway.bnplEnabled
  );

  let ListComponent;

  if (sidebarIndex === 1) {
    ListComponent = (
      <List
        key="zone"
        index="zone"
        currentSort={options.selectedSort}
        pricing={currentZone?.priceRange}
        zones={selectableZones}
        selected={currentZone}
      />
    );
  } else if (sidebarIndex === 2) {
    ListComponent = (
      <List
        key="section"
        index="section"
        currentSort={options.selectedSort}
        pricing={currentZone?.priceRange}
        zones={currentZone}
        selected={currentSection}
      />
    );
  }
  if (sidebarIndex === 3) {
    ListComponent = (
      <Pricing
        key="pricing"
        type={"event"}
        bookingFee={event.bookingFee}
        bookingFeeType={event.bookingFeeType}
        changingSeatsFee={event.changingSeatsFee}
        addons={changeTickets?.length > 0 ? undefined : addons}
        changeTickets={changeTickets}
        navigateCheckout={navigateCheckout}
        isNavigating={busyState === "creating-order"}
        onBack={() => void handleBackButton()}
        bnplGateways={bnplGateways}
        stripeAfterpayGateway={stripeAfterpayGateway}
        priceComp={priceComp}
        allowPublicComp={allowPublicComp}
        activeReferralCampaign={activeReferralCampaign}
      />
    );
  }

  const breadcrumbs = (() => {
    // @todo: later on let them choose namings aisle vs section

    let crumbs = "";

    // if (currentSection?.name) {
    //   crumbs = currentSection?.name + ", ";
    // }

    if (currentZone?.name) {
      crumbs += currentZone?.name + ", ";
    }

    crumbs += event.venue.name;

    if (!currentZone) {
      crumbs += " zones";
    }
    return crumbs;
  })();

  if (!chartReady && !selectableZones?.length) {
    return <Placeholder />;
  }

  return (
    <Box
      p={{ _: 2, md: 4 }}
      pb={2}
      height={{ _: "calc(100% - 342px)", sm: "calc(100% - 530px)", md: "100%" }}
      maxHeight={{
        _: "calc(100% - 342px)",
        sm: "calc(100% - 530px)",
        md: "100%",
      }}
      overflowY="auto"
      backgroundColor="white"
    >
      {event.isR18 && (
        <Flex
          alignItems="center"
          borderRadius="sm"
          backgroundColor="error25"
          px="6/4"
          py={2}
          mb={2}
        >
          <Icon icon="r18" fontSize={6} mr={1} />
          <Text
            fontWeight="medium"
            fontSize={2}
            lineHeight="medium"
            color="N700"
          >
            This is an R18 event
          </Text>
        </Flex>
      )}
      <Flex mb={{ _: "6/4", md: 2 }} alignItems="center" minH={32}>
        {sidebarIndex > 1 && (
          <StyledRoundedButton
            bg="white"
            onClick={() => void handleBackButton()}
            mr="6/4"
            d={{ _: "none", md: "block" }}
            py={0}
            flexShrink={0}
          >
            <Icon fontSize={6} icon="chevron-left" />
          </StyledRoundedButton>
        )}
        <Text
          fontWeight="heavy"
          fontSize={4}
          color="N800"
          // @ts-expect-error value override
          lineHeight={1.2}
        >
          {breadcrumbs}
        </Text>
      </Flex>
      <Divider />

      {!!changeTickets?.length && (
        <OriginalSeatBox mt={2}>
          <Text
            color="N800"
            fontWeight="extraBold"
            fontSize={3}
            lineHeight={"18px" as any}
          >
            Your original seat{changeTickets?.length > 1 ? "s" : ""}
          </Text>
          {changeTickets?.map((changeTicket) => (
            <Text
              key={changeTicket.id}
              fontSize={1}
              lineHeight="high"
              color="N600"
              mt="1/4"
              fontWeight="medium"
            >
              {formatSeatLocation(
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                changeTicket.seatZone,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                changeTicket.seatSection,
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                changeTicket.seatLabel
              )}
            </Text>
          ))}
        </OriginalSeatBox>
      )}
      <Box py={1} mt={{ _: "1/2", md: 2 }}>
        {selectableZones?.length > 0 && ListComponent}

        {!selectableZones?.length && <Text>No options available.</Text>}
        {options.filterActive && (
          <>
            <SecondaryButton
              w="100%"
              mt={3}
              onClick={() => {
                void actions.updateOptions({
                  options: {
                    filterActive: false,
                    seatSelectType: SeatSelectTypes.QUICK_SELECT,
                    coverType: CoverTypes.ALL,
                    zoneType: AlcoholFreeZoneTypes.NO_PREFERENCE,
                  },
                });
              }}
            >
              Reset filters
            </SecondaryButton>
          </>
        )}

        {sidebarIndex !== 3 && !!seats?.length && (
          <>
            <PrimaryButton
              isLoading={busyState === "creating-order"}
              mt={2}
              w="100%"
              disabled={validSeatSelection === false && !isAdmin && !isPOS}
              onClick={() => void navigateCheckout()}
            >
              {validSeatSelection === true || isAdmin || isPOS
                ? `Buy ${seats?.length} selected ticket${
                    seats?.length > 1 ? "s" : ""
                  }`
                : "Can not leave a single seat"}
            </PrimaryButton>
          </>
        )}
      </Box>
    </Box>
  );
};

export default SidebarLayout;
